'use client'

/**
 * Root error boundary. Catches errors thrown in the root layout itself —
 * anything the segment-level `error.tsx` cannot reach.
 *
 * This component replaces the root layout entirely when it renders, so it has
 * to supply its own <html>/<body> and pull in globals.css for the design
 * tokens.
 */

import { useEffect } from 'react'

import { AlertTriangle } from 'lucide-react'

import * as Sentry from '@sentry/nextjs'

import './globals.css'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <html lang="en">
      <body>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto rounded-full bg-muted flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-muted-foreground" />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-foreground mb-4">Something went wrong</h1>
            <p className="text-lg text-muted-foreground mb-8">
              An unexpected error stopped the page from loading. Our team has been notified.
            </p>

            <button
              onClick={() => reset()}
              className="w-full btn-primary flex items-center justify-center touch-manipulation min-h-[48px]"
            >
              Try again
            </button>

            {error.digest ? (
              <p className="mt-8 text-sm text-muted-foreground">
                Reference: <span className="font-mono">{error.digest}</span>
              </p>
            ) : null}
          </div>
        </div>
      </body>
    </html>
  )
}
